import React from 'react';
import { ProfileImage } from 'components/ProfileImage';
import dayjs from 'dayjs';
import { UserWorkout, WorkoutComment } from 'hevy-shared';
import { Link } from 'components/Link';
import { stores } from 'state/stores';
import styled from 'styled-components';
import { H6, P2 } from 'styleguide/Texts';
import { View } from 'styleguide/View';
import { getRelativeDate } from 'utils/dates';
import { CommentOptionsMenu } from './CommentOptionsMenu';
import { Spacing } from 'styleguide/spacing';
import { modal } from 'components/Modals/ModalManager';
import { LinkableText } from 'components/LinkableText';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  word-break: break-word;
`;

const CommentContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

interface CommentsProps {
  comments: WorkoutComment[];
  workout?: UserWorkout;
  onCommentDelete: (commentId: number) => void;
  onUserClick?: () => void;
  showAllComments?: boolean;
}

export const Comments = ({
  comments,
  workout,
  onCommentDelete,
  onUserClick,
  showAllComments,
}: CommentsProps) => {
  const commentsToShow = showAllComments ? comments : comments.slice(0, 2);

  return (
    <Container>
      {commentsToShow.map(c => (
        <Comment
          key={c.id}
          comment={c}
          onCommentDelete={() => onCommentDelete(c.id)}
          onUserClick={onUserClick}
        />
      ))}
      {comments.length > 2 && !showAllComments && !!workout && (
        <P2
          type="secondary"
          style={{ cursor: 'pointer' }}
          onClick={() => modal.openCommentModal(workout.id)}
        >
          {t('web.workoutCell.comments.viewAll', { numComments: comments.length })}
        </P2>
      )}
    </Container>
  );
};

interface CommentProps {
  comment: WorkoutComment;
  onCommentDelete: () => void;
  onUserClick?: () => void;
}

const Comment = ({ comment, onCommentDelete, onUserClick }: CommentProps) => {
  return (
    <CommentContainer key={`${comment.id}`}>
      <Link href={`/user/${comment.username}`} onClick={onUserClick}>
        <ProfileImage source={comment.profile_pic} diameter={48} />
      </Link>
      <View style={{ flex: 1, justifyContent: 'center', marginInlineStart: Spacing.md }}>
        <FlexRow style={{ gap: Spacing.xs }}>
          <Link href={`/user/${comment.username}`} onClick={onUserClick}>
            <H6>{comment.username}</H6>
          </Link>
          <P2 type="secondary">{' · ' + getRelativeDate(dayjs(comment.created_at))}</P2>
        </FlexRow>

        <View
          style={{
            flex: 1,
            height: 30,
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <LinkableText
            text={comment.comment}
            enableLinks
            enableMentions
            onMentionClick={onUserClick}
          />
          {comment.username === stores.account.username && (
            <View>
              <CommentOptionsMenu onDeleteClick={onCommentDelete} />
            </View>
          )}
        </View>
      </View>
    </CommentContainer>
  );
};
